import { motion } from "framer-motion";
import { Sparkles, Heart, ArrowRight, Palette, Users2, HandHeart } from "lucide-react";

/**
 * Bloc éditorial "J'adopte un artiste" — landing chaleureuse
 * avant l'ouverture du formulaire. Utilisé sur /rejoindre#adopte.
 *
 * Objectif : ne plus arriver "sur une fiche froide". Poser le geste,
 * dire pourquoi, montrer 3 promesses concrètes, puis inviter à remplir.
 */
export default function AdoptionSection({ onOpen }) {
  return (
    <section
      id="adopte"
      className="mt-32 md:mt-40 px-6 md:px-12 max-w-[1500px] mx-auto scroll-mt-32"
      data-testid="adoption-section"
    >
      {/* Header */}
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <div className="meta-label text-cinabre inline-flex items-center gap-2">
            <Sparkles size={12} strokeWidth={1.5} />
            § J'adopte un artiste · Le geste intime du mécène
          </div>
          <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
            Faire route <em className="italic">avec quelqu'un.</em>
          </h2>
          <p className="mt-8 font-serif text-xl md:text-2xl italic text-fg/85 leading-[1.4] max-w-2xl">
            « Adopter un artiste, ce n'est pas acheter une œuvre. C'est choisir un regard sur le monde et lui offrir l'espace de se déployer. »
          </p>
        </div>
        <div className="md:col-span-5 self-end">
          <p className="text-fg/80 leading-relaxed">
            Un·e mécène, un·e artiste. Vous choisissez celui ou celle dont la démarche vous touche — nous vous présentons quelques univers qui vous ressemblent, et vous décidez librement du prolongement. Aucune obligation, aucune pression : ce premier échange est fait pour vous éclairer.
          </p>
          <p className="mt-4 text-fg/70 text-sm leading-relaxed">
            Vous serez accompagné·e personnellement, par échange direct — pas de plateforme, pas de formulaire administratif. Un mail, un rendez-vous, une visite d'atelier si le cœur vous en dit.
          </p>
        </div>
      </div>

      {/* 3 promesses */}
      <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-8">
        {[
          {
            Icon: Heart,
            title: "Un lien réel, pas transactionnel",
            body: "Rencontre, correspondance, atelier, éventuellement acquisition — vous construisez la relation à votre rythme. Rien n'est imposé, tout se négocie avec délicatesse.",
          },
          {
            Icon: Palette,
            title: "Une œuvre qui porte votre trace",
            body: "Selon votre engagement : mention discrète sur le cartel, catalogue nominatif, mention gravée sur le parcours, ou simplement la joie silencieuse d'avoir permis l'œuvre.",
          },
          {
            Icon: HandHeart,
            title: "Jusqu'à 60 % de déduction fiscale",
            body: "Pour les entreprises (loi Aillagon), 66 % pour les particuliers (Cerfa fourni). Votre engagement compte deux fois : pour l'artiste, pour le fil.",
          },
        ].map((p, i) => (
          <motion.div
            key={p.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.08 }}
            className="border border-fg/15 p-8 md:p-10 hover:border-cinabre transition-colors"
            data-testid={`adopt-promise-${i}`}
          >
            <p.Icon size={22} strokeWidth={1.3} className="text-cinabre" />
            <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight mt-6">{p.title}</h3>
            <p className="mt-4 text-fg/70 leading-relaxed text-[15px]">{p.body}</p>
          </motion.div>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-14 md:mt-20 border-t border-fg/15 pt-12 md:pt-16">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="meta-label text-fg/50">§ Prochaine étape</div>
            <h3 className="mt-4 font-serif font-light text-3xl md:text-5xl leading-[1] tracking-tight">
              Dites-nous ce qui vous <em className="italic">touche.</em>
            </h3>
            <p className="mt-5 text-fg/70 leading-relaxed max-w-2xl">
              Une fiche courte, chaleureuse, à remplir en 3 minutes. Vous choisissez les univers, les géographies, les profils d'artistes qui vous parlent. Nous vous rappelons sous 5 jours ouvrés pour vous présenter <em>quelques artistes qui vous ressemblent</em>.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <button
              type="button"
              onClick={onOpen}
              className="inline-flex items-center gap-3 bg-cinabre text-bg px-8 py-5 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-fg transition-colors group"
              data-testid="adopt-open-form"
            >
              Remplir la fiche d'adoption
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <div className="mt-4 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
              3 min · aucune obligation
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
